/**
 * ListExtractorConfig Component
 *
 * Form-based configuration editor for list extractor automation.
 * Provides fields for URL, list selectors, pagination method, and pagination selectors.
 *
 * @param {Object} props
 * @param {Object} props.config - Current list extractor configuration
 * @param {Function} props.onChange - Called with updated config
 */

import React from "react";
import { FiLink, FiList, FiNavigation } from "react-icons/fi";
import SelectorArrayEditor from "./SelectorArrayEditor";

const PAGINATION_METHODS = [
  { value: "scroll", label: "Auto-Scroll" },
  { value: "navigate", label: "Page Navigation (Next button)" },
  { value: "click_button", label: "Click Load More Button" },
];

const getPaginationDescription = (method) => {
  const descriptions = {
    scroll: "The page will be scrolled automatically to load more content",
    navigate: "Selector for the 'Next Page' button or link",
    click_button: "Selector for the 'Load More' button to click",
  };
  return descriptions[method] || "";
};

const ListExtractorConfig = ({ config = {}, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...config, [field]: value });
  };

  const paginationMethod = config.paginationMethod || "none";
  const showPaginationSelectors = paginationMethod !== "none" && paginationMethod !== "scroll";

  return (
    <div className="space-y-2.5">
      {/* URL Input Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-500/15 mr-2">
            <FiLink className="text-blue-500 dark:text-blue-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Starting URL</h3>
        </div>
        <div className="space-y-2">
          <input
            type="url"
            value={config.url || ""}
            onChange={(e) => updateField("url", e.target.value)}
            placeholder="https://example.com/products"
            className="w-full bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 py-2 rounded-lg text-sm
                       border border-gray-300 dark:border-gray-600/50 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none
                       placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
          />
          <p className="text-2xs text-gray-400 dark:text-gray-500">The page URL where the list extraction will start</p>
        </div>
      </div>

      {/* List Selectors Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 mr-2">
            <FiList className="text-emerald-500 dark:text-emerald-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">List Selectors</h3>
        </div>
        <SelectorArrayEditor
          selectors={config.listSelectors || []}
          onChange={(selectors) => updateField("listSelectors", selectors)}
          description="CSS or XPath selectors to identify list items on the page"
          emptyMessage="No list selectors defined. Add a selector to identify list items."
        />
      </div>

      {/* Pagination Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-500/15 mr-2">
            <FiNavigation className="text-amber-500 dark:text-amber-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Pagination</h3>
        </div>

        <div className="space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Method</label>
            <div className="relative">
              <select
                value={paginationMethod}
                onChange={(e) => updateField("paginationMethod", e.target.value)}
                className="appearance-none w-full bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 pr-8 py-2 rounded-lg text-sm
                           border border-gray-300 dark:border-gray-600/50 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none
                           cursor-pointer transition-colors"
              >
                {PAGINATION_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
              <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {paginationMethod !== "none" && (
              <p className="text-2xs text-gray-400 dark:text-gray-500">{getPaginationDescription(paginationMethod)}</p>
            )}
          </div>

          {/* Pagination Selectors - shown for navigate and click_button methods */}
          {showPaginationSelectors && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700/30">
              <SelectorArrayEditor
                selectors={config.paginationSelectors || []}
                onChange={(selectors) => updateField("paginationSelectors", selectors)}
                label="Pagination Selectors"
                description={
                  paginationMethod === "navigate"
                    ? "Selectors for the 'Next' button or pagination links"
                    : "Selectors for the 'Load More' button"
                }
                emptyMessage="Add a selector for the pagination element."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListExtractorConfig;
